// LARGE COMPONENT
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import { REPORT_INFO_TYPES, REPORT_STATUSES } from 'constants/reports';
import dayjs from 'dayjs';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { CREATE_REPORT, UPDATE_REPORT } from 'gql/reports/mutations';
import { CreateReports } from 'gql/reports/__generated__/CreateReports';
import { useUIDispatchContext } from 'providers/UI';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import client from 'apolloClient';
import { GET_REPORTS, GET_REPORT_BY_ID } from 'gql/reports/queries';
import { GetReportById } from 'gql/reports/__generated__/GetReportById';
import { UpdateReports } from 'gql/reports/__generated__/UpdateReports';
import {
  useReportValuesDispatchContext,
  useReportValuesStateContext
} from 'providers/ReportValues';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';
import { PaymentAccountDoctor } from 'gql/profile/__generated__/PaymentAccountDoctor';
import { GET_PAYMENT_ACCOUNT } from 'gql/profile/queries';
import { setTour } from 'store/tour/slice';
import { useAppDispatch } from 'store/hooks';

import { Loading, PageWrapper } from 'components/ui';
import DoctorInfo from './components/DoctorInfo';
import LivePreview from './components/LivePreview';
import PatientInfo from './components/PatientInfo';
import { showErrorMessage } from 'utils/showErrorMessage';
import { HowItWorksLink } from 'components/shared';

const CreateUpdateReport: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleModal } = useUIDispatchContext();
  const params = useParams();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const appointmentId = searchParams.get('id');

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(!!params.id);

  const values = useReportValuesStateContext();
  const { setValues } = useReportValuesDispatchContext();

  const [signature, setSignature] = useState('');

  const [createReport, { loading: createLoading }] = useMutation<CreateReports>(
    CREATE_REPORT,
    {
      onCompleted() {
        if (appointmentId) {
          navigate('/report/medicus');
        } else {
          navigate('/report/personal');
        }

        client.refetchQueries({ include: [GET_REPORTS] });
      },
      onError: err => showErrorMessage(err)
    }
  );

  const [updateReport, { loading: updateLoading }] = useMutation<UpdateReports>(
    UPDATE_REPORT,
    {
      onCompleted() {
        if (appointmentId) {
          navigate('/report/medicus');
        } else {
          navigate('/report/personal');
        }

        client.refetchQueries({ include: [GET_REPORTS] });
      },
      onError: err => showErrorMessage(err)
    }
  );

  const [getPaymentAccount] = useLazyQuery<PaymentAccountDoctor>(
    GET_PAYMENT_ACCOUNT,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      onCompleted(data) {
        if (data.paymentAccountDoctor?.data?.signature) {
          const value = data.paymentAccountDoctor.data.signature;

          setSignature(value);
        }

        setLoading(false);
        setTimeout(() => {
          dispatch(setTour({ key: 'addReport', value: true }));
        });
      }
    }
  );

  useQuery<GetReportById>(GET_REPORT_BY_ID, {
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      const reportData = data.getReportById?.data;

      if (!reportData) {
        return;
      }

      setValues({
        date_of_birth: dayjs(reportData.date_of_birth),
        report: reportData.report,
        last_name: reportData.last_name,
        report_date: dayjs(reportData.date_report),
        email: reportData.email,
        doctor_info: reportData.doctor_info,
        first_name: reportData.first_name,
        lang: reportData.lang,
        icd: reportData.icd || []
      });

      if (reportData.signature) {
        setSignature(reportData.signature);
        setLoading(false);
        setTimeout(() => {
          dispatch(setTour({ key: 'addReport', value: true }));
        });
      } else {
        getPaymentAccount();
      }
    },
    variables: {
      id: params.id
    },
    skip: !params.id
  });

  useEffect(() => {
    if (!params.id) {
      getPaymentAccount();
    }
  }, [getPaymentAccount, params.id]);

  const input = useMemo(
    () => ({
      date_of_birth: values.date_of_birth,
      email: values.email || undefined,
      first_name: values.first_name,
      last_name: values.last_name,
      report: values.report,
      date_report: values.report_date,
      doctor_info: values.doctor_info,
      lang: values.lang,
      signature: signature,
      icd: values.icd
    }),
    [signature, values]
  );

  const onSave = useCallback(
    (type: string) => {
      if (params.id && !appointmentId) {
        return updateReport({
          variables: {
            input: {
              ...input,
              status: type
            },
            id: params?.id
          }
        });
      }

      createReport({
        variables: {
          input: {
            ...input,
            appointment_id: appointmentId || undefined,
            status: type
          }
        }
      });
    },
    [params.id, appointmentId, createReport, input, updateReport]
  );

  const onSaveDraft = useCallback(
    (type: string) => {
      if (params.id && !appointmentId) {
        return updateReport({
          variables: {
            input: {
              ...input,
              status: type
            },
            id: params?.id
          }
        });
      }

      createReport({
        variables: {
          input: {
            ...input,
            appointment_id: appointmentId || undefined,
            status: type
          }
        }
      });
    },
    [appointmentId, input, params.id, createReport, updateReport]
  );

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <PatientInfo
            signature={signature}
            appointmentId={appointmentId as string}
            loading={createLoading || updateLoading}
            onSignChange={setSignature}
            onSaveDraft={() => onSaveDraft(REPORT_STATUSES.draft)}
            onClickNext={() => setStep(1)}
          />
        );
      default:
        return (
          <DoctorInfo
            loading={createLoading || updateLoading}
            onClickBack={() => setStep(0)}
            onSave={() => onSave(REPORT_STATUSES.sent)}
            onSaveDraft={() => onSaveDraft(REPORT_STATUSES.draft)}
          />
        );
    }
  };

  const onClickPreview = () => {
    toggleModal('reportPreview', true, {
      date: dayjs(values?.report_date).format() as string,
      aboutDoctor: values?.doctor_info as string,
      report: values?.report as string,
      patientBirth: dayjs(values?.date_of_birth).format() as string,
      patientName: values?.first_name as string,
      patientLastName: values?.last_name as string,
      appointmentId: appointmentId as string,
      signature,
      lang: values.lang,
      icd: values.icd
    });
  };

  if (loading || (params.id && !Object.values(values).some(item => item))) {
    return <Loading />;
  }

  return (
    <PageWrapper
      back
      title={t(`reports.add_report`)}
      style={{ padding: 0 }}
      extra={[
        <HowItWorksLink
          key="how-it-works"
          type={DOCTOR_HOW_IT_WORKS_PAGES.REPORT.key}
          title={t('common.how_it_works')}
        />
      ]}
    >
      <Row style={{ height: '100%' }}>
        <Col span={12} style={{ backgroundColor: 'var(--white)' }}>
          {renderStep()}
        </Col>
        <Col span={12} style={{ backgroundColor: '#F6F7FB' }}>
          <LivePreview
            type={
              step === 0 ? REPORT_INFO_TYPES.patient : REPORT_INFO_TYPES.doctor
            }
            content={
              step === 0 ? values?.report || '' : values.doctor_info || ''
            }
            signature={signature}
            appointmentId={appointmentId}
            step={step}
            onClickPreview={onClickPreview}
          />
        </Col>
      </Row>
    </PageWrapper>
  );
};

export default CreateUpdateReport;
